import { X } from 'lucide-react'
import PropTypes from 'prop-types'
import { useCallback, useState } from 'react'

import { AdasBox, AdasButton, AdasModal, AdasPaper, AdasTextField, AdasTypography } from '@components/wrapper-components'
import { BUTTONS_DISPLAY_LABEL, GENERIC_MESSAGES } from '../../../constants'
import theme from 'theme/theme'

export const ConfirmModal = ({
  open,
  handleClose,
  handleContinue,
  title,
  message,
  cancelLabel = BUTTONS_DISPLAY_LABEL.CANCEL,
  confirmLabel,
  typeCheck,
  warningMessage,
  adminCheck,
  adminCheckFailureMessage,
  notifyError
}) => {
  const [code, setCode] = useState('')
  const handleModalClose = useCallback(() => {
    setCode('')
    handleClose()
  }, [handleClose])

  const handleSubmit = useCallback(() => {
    handleContinue(code)
    setCode('')
  }, [handleContinue, code])

  return (
    <AdasModal
      open={open}
      onClose={handleModalClose}
      aria-labelledby='modal-modal-title'
      aria-describedby='modal-modal-description'
      className='flex items-center justify-center'
    >
      <AdasPaper variant='outlined' className='min-w-0 sm:min-w-[450px] flex flex-col relative p-[20px] max-w-[600px] min-h-[200px]'>
        <AdasButton
          buttonType='iconButton'
          className='text-base cursor-pointer absolute p-0.5 top-[10px] right-[10px]'
          onClick={handleModalClose}
        >
          <X style={{ color: theme.palette.info.main }} />
        </AdasButton>
        <AdasTypography variant='h6' className='font-bold text-lg text-left leading-[28px] mb-[20px] mt-0'>
          {title}
        </AdasTypography>
        {warningMessage && (
          <AdasTypography
            variant='body1'
            className='font-normal text-base text-left leading-5 mt-[20px] mb-0 tracking-[0.15000000596046448px]'
          >
            {warningMessage}
          </AdasTypography>
        )}
        {typeof message === 'string' ? (
          <AdasTypography
            variant='body1'
            className='font-normal text-base text-left leading-5 my-[20px] mx-0 tracking-[0.15000000596046448px]'
          >
            {message}
          </AdasTypography>
        ) : (
          <>{message}</>
        )}
        {!notifyError && typeCheck && (
          <AdasTextField
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            name='type check'
            id='typeCheck'
            label={`Type ‘${typeCheck}’`}
            variant='outlined'
            fullWidth={true}
            InputLabelProps={{
              shrink: true
            }}
          />
        )}
        {adminCheck && (
          <>
            <AdasTextField
              onChange={(e) => setCode(e.target.value)}
              name='Admin Code'
              id='adminCodeInput'
              label={GENERIC_MESSAGES.SYSTEM_ADMIN_CODE}
              variant='outlined'
              fullWidth={true}
              InputLabelProps={{
                shrink: true
              }}
            />
            {adminCheckFailureMessage && (
              <AdasTypography className='text-error text-xs mx-[5px] my-0.5'>{adminCheckFailureMessage}</AdasTypography>
            )}
          </>
        )}
        {!notifyError && (
          <AdasBox className='flex justify-end mt-[20px]'>
            <AdasButton className='mr-[5px]' variant='outlined' onClick={handleModalClose}>
              {cancelLabel}
            </AdasButton>
            <AdasButton disabled={typeCheck ? !(typeCheck === code) : adminCheck && !code} onClick={handleSubmit} variant='contained'>
              {confirmLabel}
            </AdasButton>
          </AdasBox>
        )}
      </AdasPaper>
    </AdasModal>
  )
}

ConfirmModal.propTypes = {
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  handleContinue: PropTypes.func.isRequired,
  title: PropTypes.string.isRequired,
  message: PropTypes.oneOfType([PropTypes.string, PropTypes.object]).isRequired,
  confirmLabel: PropTypes.string.isRequired,
  typeCheck: PropTypes.string
}
